import { useCallback, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import axios from "axios";
import { saveAs } from "file-saver";
import { Download, FileText, Trash2, Upload } from "lucide-react";

const formatFileSize = (bytes) => {
    const size = Number(bytes);
    if (!size || size <= 0) return "";
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const normalizeFile = (file) => ({
    id: file.id,
    originalName: file.original_name || file.originalName || "Untitled file",
    locationFile: file.location_file || file.locationFile || "",
    fileSize: file.file_size ?? file.fileSize ?? 0,
    contentType: file.content_type || file.contentType || "",
});

const VendorAttachmentsSection = ({
    files = [],
    vendorId,
    access = "edit",
    onFilesChange,
}) => {
    const [items, setItems] = useState(() => files.map(normalizeFile));
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setItems(files.map(normalizeFile));
    }, [files]);

    const uploadFiles = useCallback(async (acceptedFiles) => {
        if (access !== "edit" || !vendorId || !acceptedFiles.length) return;

        setLoading(true);
        try {
            const uploaded = await Promise.all(acceptedFiles.map(async (file) => {
                const formData = new FormData();
                formData.append("file", file);
                formData.append("referenceType", "Vendor");
                formData.append("referenceId", vendorId);

                const { data } = await axios.post("/api/v1/files/upload", formData, {
                    headers: { "Content-Type": "multipart/form-data" },
                });

                return normalizeFile(data.file);
            }));

            setItems((prev) => {
                const nextItems = [...uploaded, ...prev];
                onFilesChange?.(nextItems);
                return nextItems;
            });
        } catch (error) {
            console.error("Error uploading vendor attachment:", error);
        } finally {
            setLoading(false);
        }
    }, [access, vendorId, onFilesChange]);

    const onDrop = useCallback((acceptedFiles) => {
        uploadFiles(acceptedFiles);
    }, [uploadFiles]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        multiple: true,
        disabled: loading || access !== "edit",
    });

    const handleDownload = async (file) => {
        if (!file.locationFile) return;

        try {
            const response = await fetch(file.locationFile, { mode: "cors" });
            const blob = await response.blob();
            saveAs(blob, file.originalName);
        } catch (error) {
            console.error("Error downloading file:", error);
            window.open(file.locationFile, "_blank", "noopener,noreferrer");
        }
    };

    const handleDelete = async (fileId) => {
        if (access !== "edit") return;

        setLoading(true);
        try {
            await axios.delete(`/api/v1/files/delete/${fileId}`);
            setItems((prev) => {
                const nextItems = prev.filter((file) => file.id !== fileId);
                onFilesChange?.(nextItems);
                return nextItems;
            });
        } catch (error) {
            console.error("Error deleting file:", error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-2">
            {items.length > 0 && (
                <div className="space-y-1.5">
                    {items.map((file) => {
                        const canDownload = Boolean(file.locationFile);

                        return (
                            <div
                                key={file.id}
                                className="flex items-center gap-2 px-2.5 py-1.5 rounded-md bg-gray-100 group"
                            >
                                <FileText className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                                {canDownload ? (
                                    <a
                                        href={file.locationFile}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-[12px] text-foreground truncate flex-1 hover:underline"
                                    >
                                        {file.originalName}
                                    </a>
                                ) : (
                                    <span className="text-[12px] text-muted-foreground truncate flex-1">
                                        {file.originalName}
                                    </span>
                                )}
                                {file.fileSize > 0 && (
                                    <span className="text-[11px] text-muted-foreground whitespace-nowrap">
                                        {formatFileSize(file.fileSize)}
                                    </span>
                                )}
                                <button
                                    type="button"
                                    disabled={!canDownload || loading}
                                    onClick={() => handleDownload(file)}
                                    className="p-0.5 rounded text-muted-foreground hover:text-foreground opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-20 disabled:cursor-not-allowed"
                                    title={canDownload ? "Download" : "No file data"}
                                >
                                    <Download className="w-3 h-3" />
                                </button>
                                {access === "edit" && (
                                    <button
                                        type="button"
                                        disabled={loading}
                                        onClick={() => handleDelete(file.id)}
                                        className="p-0.5 rounded text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
                                        title="Remove"
                                    >
                                        <Trash2 className="w-3 h-3" />
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {access === "edit" && vendorId && (
                <div
                    {...getRootProps()}
                    className={`w-full flex items-center justify-center gap-2 py-4 border border-dashed rounded-md text-[12px] transition-colors cursor-pointer ${
                        isDragActive
                            ? "border-foreground/30 text-foreground bg-gray-50"
                            : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/30"
                    } ${loading ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                    <input {...getInputProps()} />
                    <Upload className="w-3.5 h-3.5" />
                    {loading ? "Uploading..." : "Click to upload or drag & drop"}
                </div>
            )}
        </div>
    );
};

export default VendorAttachmentsSection;
